import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Pencil, Trash2, X, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAstrologerProfile } from '../../hooks/useAstrologerProfile';
import type {
  AstrologerClient, AstrologerAnalysis, AstrologerNote,
  AstrologerClientRelationship, RelationshipType,
} from '../../types/professional';
import { PRO_SERVICES, RELATIONSHIP_LABELS } from '../../types/professional';
import { useToast } from '../ui/ToastSystem';
import { PhoneInput } from '../ui/PhoneInput';
import { BirthDateInput } from '../ui/BirthDateInput';
import { BirthTimeInput } from '../ui/BirthTimeInput';
import { CityAutocomplete } from '../ui/CityAutocomplete';

const TABS = ['Perfil', 'Análises', 'Relacionamentos', 'Notas'] as const;
type Tab = typeof TABS[number];

function StatusBadge({ status }: { status: AstrologerAnalysis['status'] }) {
  const map = {
    completed:  { label: '✅ Completo',    color: '#4ade80' },
    processing: { label: '⏳ Processando', color: '#fbbf24' },
    pending:    { label: '⏳ Pendente',    color: '#fbbf24' },
    error:      { label: '❌ Erro',        color: '#f87171' },
  };
  const s = map[status] ?? { label: status, color: 'var(--text-muted)' };
  return <span style={{ fontSize: 'var(--text-caption)', color: s.color }}>{s.label}</span>;
}

export function ProfessionalClientProfile() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate     = useNavigate();
  const [sp]         = useSearchParams();
  const { astrologerProfile } = useAstrologerProfile();
  const { showToast } = useToast();

  const tabParam = sp.get('tab') ?? '';
  const [activeTab,   setActiveTab]   = useState<Tab>(
    tabParam === 'analises'        ? 'Análises'       :
    tabParam === 'relacionamentos' ? 'Relacionamentos' :
    tabParam === 'notas'           ? 'Notas'           : 'Perfil',
  );
  const [client,      setClient]      = useState<AstrologerClient | null>(null);
  const [analyses,    setAnalyses]    = useState<AstrologerAnalysis[]>([]);
  const [notes,       setNotes]       = useState<AstrologerNote[]>([]);
  const [noteText,    setNoteText]    = useState('');
  const [loading,     setLoading]     = useState(true);
  const [generating,  setGenerating]  = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  // Relationships state
  const [relationships,   setRelationships]   = useState<AstrologerClientRelationship[]>([]);
  const [allClients,      setAllClients]       = useState<AstrologerClient[]>([]);
  const [showRelForm,     setShowRelForm]      = useState(false);
  const [relClientId,     setRelClientId]      = useState('');
  const [relType,         setRelType]          = useState<RelationshipType>('conjuge');
  const [relNotes,        setRelNotes]         = useState('');
  const [savingRel,       setSavingRel]        = useState(false);

  // Edit / delete state
  const [editMode,        setEditMode]         = useState(false);
  const [editData,        setEditData]         = useState<Partial<AstrologerClient>>({});
  const [editTagsText,    setEditTagsText]      = useState('');
  const [saving,          setSaving]           = useState(false);

  const fetchAll = useCallback(async () => {
    if (!clientId || !astrologerProfile) return;
    const [{ data: c }, { data: a }, { data: n }, { data: rA }, { data: rB }, { data: cls }] = await Promise.all([
      supabase.from('astrologer_clients').select('*').eq('id', clientId).single(),
      supabase.from('astrologer_client_analyses').select('*').eq('client_id', clientId).order('created_at', { ascending: false }),
      supabase.from('astrologer_client_notes').select('*').eq('client_id', clientId).order('created_at', { ascending: false }),
      // Relationships where this client is client_a
      supabase.from('astrologer_client_relationships').select('*, related_client:astrologer_clients!client_b_id(id, name, birth_date)').eq('client_a_id', clientId),
      // Relationships where this client is client_b
      supabase.from('astrologer_client_relationships').select('*, related_client:astrologer_clients!client_a_id(id, name, birth_date)').eq('client_b_id', clientId),
      supabase.from('astrologer_clients').select('id, name, birth_date').eq('astrologer_id', astrologerProfile.id).eq('is_active', true).order('name'),
    ]);
    setClient(c as AstrologerClient | null);
    setAnalyses((a ?? []) as AstrologerAnalysis[]);
    setNotes((n ?? []) as AstrologerNote[]);
    setRelationships([...(rA ?? []), ...(rB ?? [])] as AstrologerClientRelationship[]);
    setAllClients((cls ?? []) as AstrologerClient[]);
    setLoading(false);
  }, [clientId, astrologerProfile?.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-generate if URL params request it
  useEffect(() => {
    const svcSlug = sp.get('service');
    const cid2    = sp.get('client2Id');
    if (svcSlug && client && astrologerProfile && !generating) {
      handleGenerate(svcSlug, cid2 ?? undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id, sp.toString()]);

  async function handleGenerate(serviceSlug: string, client2Id?: string) {
    if (!client || !astrologerProfile) return;
    const svc = PRO_SERVICES.find(s => s.slug === serviceSlug);
    if (!svc) return;

    setGenerating(serviceSlug);
    setActiveTab('Análises');
    try {
      const { data } = await supabase.functions.invoke('astrologer-generate-analysis', {
        body: {
          astrologer_id: astrologerProfile.id,
          client_id:     client.id,
          service_slug:  serviceSlug,
          client2_id:    client2Id ?? null,
        },
      });
      if (data?.ok) {
        showToast({ message: `Análise de ${client.name} gerada com sucesso ✦`, type: 'success' });
        await fetchAll();
      } else {
        showToast({ message: data?.error ?? 'Erro ao gerar análise', type: 'error' });
      }
    } catch (err) {
      showToast({ message: (err as Error).message, type: 'error' });
    } finally {
      setGenerating(null);
    }
  }

  async function handleDownload(analysis: AstrologerAnalysis) {
    setDownloading(analysis.id);
    try {
      const { data } = await supabase.functions.invoke('astrologer-generate-docx', {
        body: { analysis_id: analysis.id },
      });
      if (data?.ok && data.data?.url) {
        window.open(data.data.url, '_blank', 'noopener,noreferrer');
        await fetchAll();
      } else {
        showToast({ message: data?.error ?? 'Erro ao gerar documento', type: 'error' });
      }
    } catch (err) {
      showToast({ message: (err as Error).message, type: 'error' });
    } finally {
      setDownloading(null);
    }
  }

  async function handleAddNote() {
    if (!noteText.trim() || !client || !astrologerProfile) return;
    await supabase.from('astrologer_client_notes').insert({
      astrologer_id: astrologerProfile.id,
      client_id: client.id,
      content: noteText.trim(),
    });
    setNoteText('');
    await fetchAll();
  }

  async function handleAddRelationship() {
    if (!relClientId || !clientId || !astrologerProfile) return;
    setSavingRel(true);
    const { error } = await supabase.from('astrologer_client_relationships').insert({
      astrologer_id:     astrologerProfile.id,
      client_a_id:       clientId,
      client_b_id:       relClientId,
      relationship_type: relType,
      notes:             relNotes.trim() || null,
    });
    if (error) {
      showToast({ message: error.code === '23505' ? 'Relacionamento já cadastrado.' : 'Erro ao salvar relacionamento.', type: 'error' });
    } else {
      showToast({ message: 'Relacionamento adicionado ✦', type: 'success' });
      setShowRelForm(false);
      setRelClientId('');
      setRelNotes('');
      setRelType('conjuge');
      await fetchAll();
    }
    setSavingRel(false);
  }

  async function handleRemoveRelationship(relId: string) {
    await supabase.from('astrologer_client_relationships').delete().eq('id', relId);
    await fetchAll();
  }

  function handleStartEdit() {
    if (!client) return;
    setEditData({ ...client });
    setEditTagsText((client.tags ?? []).join(', '));
    setEditMode(true);
  }

  async function handleSaveEdit() {
    if (!client || !astrologerProfile || !editData.name?.trim()) return;
    setSaving(true);

    const tagList = editTagsText.split(',').map(t => t.trim()).filter(Boolean);
    const after = {
      name:               editData.name.trim(),
      email:              editData.email?.trim() || null,
      phone:              editData.phone?.trim() || null,
      whatsapp:           editData.whatsapp?.trim() || null,
      birth_date:         editData.birth_date ?? client.birth_date,
      birth_time:         editData.birth_time_unknown ? null : (editData.birth_time?.trim() || null),
      birth_time_unknown: editData.birth_time_unknown ?? false,
      birth_city:         editData.birth_city?.trim() || null,
      gender:             editData.gender || null,
      notes:              editData.notes?.trim() || null,
      tags:               tagList.length ? tagList : null,
    };

    const before = {
      name: client.name, email: client.email, phone: client.phone,
      whatsapp: client.whatsapp, birth_date: client.birth_date,
      birth_time: client.birth_time, birth_time_unknown: client.birth_time_unknown,
      birth_city: client.birth_city, gender: client.gender,
      notes: client.notes, tags: client.tags,
    };

    const changedFields = (Object.keys(after) as (keyof typeof after)[])
      .filter(k => JSON.stringify(before[k]) !== JSON.stringify(after[k]));

    const { error } = await supabase.from('astrologer_clients').update(after).eq('id', client.id);
    if (error) {
      showToast({ type: 'error', message: 'Erro ao salvar alterações.' });
    } else {
      if (changedFields.length > 0) {
        await supabase.from('astrologer_client_audit_log').insert({
          astrologer_id:  astrologerProfile.id,
          client_id:      client.id,
          action:         'update',
          changed_fields: changedFields,
          before_data:    before,
          after_data:     after,
        });
      }
      showToast({ type: 'success', message: 'Dados atualizados ✦' });
      setEditMode(false);
      await fetchAll();
    }
    setSaving(false);
  }

  async function handleDeleteClient() {
    if (!client || !astrologerProfile) return;
    if (!window.confirm(`Excluir "${client.name}"? O registro ficará inativo mas será mantido para fins de compliance. Esta ação não pode ser desfeita.`)) return;

    const { error } = await supabase.from('astrologer_clients').update({ is_active: false }).eq('id', client.id);
    if (error) {
      showToast({ type: 'error', message: 'Erro ao excluir cliente.' });
      return;
    }
    await supabase.from('astrologer_client_audit_log').insert({
      astrologer_id:  astrologerProfile.id,
      client_id:      client.id,
      action:         'delete',
      changed_fields: ['is_active'],
      before_data:    { name: client.name, email: client.email, birth_date: client.birth_date, is_active: true },
      after_data:     { is_active: false },
    });
    showToast({ type: 'success', message: `Cliente "${client.name}" excluído.` });
    navigate('/profissional/clientes');
  }

  if (loading) return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Carregando...</div>;
  if (!client)  return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Cliente não encontrado.</div>;

  const nc = client.natal_chart_json;
  const birthDateFmt = new Date(client.birth_date + 'T00:00:00').toLocaleDateString('pt-BR');

  // Clients available to link (exclude self and already-linked)
  const linkedIds = new Set(relationships.map(r => r.related_client?.id ?? ''));
  const availableClients = allClients.filter(c => c.id !== clientId && !linkedIds.has(c.id));

  return (
    <div>
      <button className="btn-secondary" style={{ fontSize: 'var(--text-caption)', marginBottom: '1rem' }}
        onClick={() => navigate('/profissional/clientes')}>
        ← Clientes
      </button>

      {/* Header */}
      <div className="card" style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ width: '56px', height: '56px', borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg, var(--petrol), var(--petrol))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.4rem', color: '#000' }}>
          {client.name.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: '0 0 0.25rem', fontSize: 'clamp(1.125rem,3vw,1.375rem)', color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>
            {client.name}
          </h1>
          <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
            {birthDateFmt}
            {client.birth_city ? ` · ${client.birth_city}` : ''}
            {nc?.sun_sign ? ` · Sol em ${nc.sun_sign}` : ''}
            {nc?.moon_sign ? ` · Lua em ${nc.moon_sign}` : ''}
            {relationships.length > 0 ? ` · ${relationships.length} relacionamento${relationships.length > 1 ? 's' : ''}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          <button
            className="btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: 'var(--text-caption)', padding: '0.4rem 0.75rem' }}
            onClick={handleStartEdit}
            title="Editar dados do cliente"
          >
            <Pencil size={13} /> Editar
          </button>
          <button
            className="btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: 'var(--text-caption)', padding: '0.4rem 0.75rem', color: '#f87171', borderColor: '#f87171' }}
            onClick={handleDeleteClient}
            title="Excluir cliente"
          >
            <Trash2 size={13} /> Excluir
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle)', marginBottom: '1.5rem', overflowX: 'auto' }}>
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '0.6rem 1rem', background: 'none', border: 'none',
            borderBottom: `2px solid ${activeTab === tab ? 'var(--petrol)' : 'transparent'}`,
            color: activeTab === tab ? 'var(--petrol)' : 'var(--text-muted)',
            cursor: 'pointer', fontWeight: activeTab === tab ? 600 : 400,
            fontSize: 'var(--text-label)', whiteSpace: 'nowrap',
          }}>
            {tab}
            {tab === 'Relacionamentos' && relationships.length > 0 && (
              <span style={{ marginLeft: '0.35rem', fontSize: '0.65rem', background: 'var(--petrol)', color: '#fff', borderRadius: '999px', padding: '0.05rem 0.4rem' }}>
                {relationships.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* TAB: Perfil */}
      {activeTab === 'Perfil' && !editMode && (
        <div className="card">
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem', fontSize: 'var(--text-label)' }}>Dados</p>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.4rem 1rem', fontSize: 'var(--text-label)' }}>
            {[
              ['E-mail',     client.email],
              ['WhatsApp',   client.whatsapp || client.phone],
              ['Nascimento', birthDateFmt + (client.birth_time && !client.birth_time_unknown ? ` às ${client.birth_time}` : '')],
              ['Local',      client.birth_city],
              ['Gênero',     client.gender],
              ['Tags',       client.tags?.join(', ')],
              ['Notas',      client.notes],
            ].filter(([, v]) => v).map(([k, v]) => (
              <>
                <dt key={`dt-${k}`} style={{ color: 'var(--text-muted)', margin: 0 }}>{k}</dt>
                <dd key={`dd-${k}`} style={{ color: 'var(--text-primary)', margin: 0 }}>{v}</dd>
              </>
            ))}
          </dl>
          {nc && (
            <>
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '1.25rem 0 0.75rem', fontSize: 'var(--text-label)' }}>Mapa Natal</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {([
                  ['Sol',      nc.sun_sign],
                  ['Lua',      nc.moon_sign],
                  ['Mercúrio', nc.mercury_sign],
                  ['Vênus',    nc.venus_sign],
                  ['Marte',    nc.mars_sign],
                  nc.ascendant ? ['Asc.', nc.ascendant] : null,
                ].filter(Boolean) as string[][]).map(([planet, sign]) => (
                  <span key={planet} style={{ background: 'var(--bg-elevated)', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
                    {planet} em {sign}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* TAB: Perfil — edit mode */}
      {activeTab === 'Perfil' && editMode && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
            <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: 0, fontSize: 'var(--text-label)' }}>Editar dados</p>
            <button onClick={() => setEditMode(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
              <X size={18} />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Name */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nome *</label>
              <input className="form-input" type="text" value={editData.name ?? ''} onChange={e => setEditData(p => ({ ...p, name: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            {/* Email */}
            <div>
              <label style={{ display: 'block', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>E-mail</label>
              <input className="form-input" type="email" value={editData.email ?? ''} onChange={e => setEditData(p => ({ ...p, email: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            {/* WhatsApp */}
            <div>
              <label style={{ display: 'block', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>WhatsApp</label>
              <PhoneInput value={editData.whatsapp ?? ''} onChange={v => setEditData(p => ({ ...p, whatsapp: v || null }))} />
            </div>
            {/* Phone */}
            <div>
              <label style={{ display: 'block', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Telefone</label>
              <PhoneInput value={editData.phone ?? ''} onChange={v => setEditData(p => ({ ...p, phone: v || null }))} />
            </div>
            {/* Birth date */}
            <div>
              <label style={{ display: 'block', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Data de nascimento *</label>
              <BirthDateInput value={editData.birth_date ?? ''} onChange={v => setEditData(p => ({ ...p, birth_date: v }))} required />
            </div>
            {/* Birth time */}
            <div>
              <label style={{ display: 'block', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Hora de nascimento</label>
              <BirthTimeInput value={editData.birth_time ?? ''} onChange={v => setEditData(p => ({ ...p, birth_time: v || null }))} disabled={!!editData.birth_time_unknown} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.4rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!editData.birth_time_unknown} onChange={e => setEditData(p => ({ ...p, birth_time_unknown: e.target.checked, birth_time: e.target.checked ? null : p.birth_time })) } />
                Hora desconhecida
              </label>
            </div>
            {/* Birth city */}
            <div>
              <label style={{ display: 'block', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cidade de nascimento</label>
              <CityAutocomplete value={editData.birth_city ?? ''} onChange={v => setEditData(p => ({ ...p, birth_city: v || null }))} placeholder="São Paulo, Brasil" />
            </div>
            {/* Gender */}
            <div>
              <label style={{ display: 'block', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gênero</label>
              <select className="form-input" value={editData.gender ?? ''} onChange={e => setEditData(p => ({ ...p, gender: e.target.value }))} style={{ width: '100%' }}>
                <option value="">Não informado</option>
                <option value="F">Feminino</option>
                <option value="M">Masculino</option>
                <option value="NB">Não-binário</option>
              </select>
            </div>
            {/* Tags */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tags (separadas por vírgula)</label>
              <input className="form-input" type="text" value={editTagsText} onChange={e => setEditTagsText(e.target.value)} placeholder="ex: vip, mensal, terapia" style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            {/* Notes */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notas internas</label>
              <textarea className="form-input" rows={3} value={editData.notes ?? ''} onChange={e => setEditData(p => ({ ...p, notes: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
            <button className="btn-secondary" style={{ fontSize: 'var(--text-caption)', display: 'flex', alignItems: 'center', gap: '0.35rem' }} onClick={() => setEditMode(false)}>
              <X size={14} /> Cancelar
            </button>
            <button
              className="btn-primary"
              style={{ fontSize: 'var(--text-caption)', display: 'flex', alignItems: 'center', gap: '0.35rem', opacity: saving ? 0.7 : 1 }}
              disabled={saving || !editData.name?.trim()}
              onClick={handleSaveEdit}
            >
              <Check size={14} /> {saving ? 'Salvando…' : 'Salvar alterações'}
            </button>
          </div>
        </div>
      )}

      {/* TAB: Análises */}
      {activeTab === 'Análises' && (
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
              Gerar nova análise:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {PRO_SERVICES.filter(s => !s.needsSecondClient).map(svc => (
                <button
                  key={svc.slug}
                  className="btn-secondary"
                  style={{ fontSize: 'var(--text-caption)', padding: '0.25rem 0.6rem' }}
                  disabled={!!generating}
                  onClick={() => handleGenerate(svc.slug)}
                >
                  {generating === svc.slug ? '...' : svc.name}
                </button>
              ))}
            </div>
            {/* Synastry quick-links using relationships */}
            {relationships.length > 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                  Análises de relacionamento:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {relationships.map(rel => rel.related_client && (
                    <button
                      key={rel.id}
                      className="btn-secondary"
                      style={{ fontSize: 'var(--text-caption)', padding: '0.25rem 0.6rem' }}
                      disabled={!!generating}
                      onClick={() => navigate(`/profissional/gerar-analise?clientId=${clientId}&service=synastry&client2Id=${rel.related_client!.id}`)}
                    >
                      Sinastria com {rel.related_client.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {analyses.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
              Nenhuma análise gerada ainda.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {analyses.map(a => (
                <div key={a.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: '0 0 0.2rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
                      {a.service_name}
                    </p>
                    <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                      {new Date(a.created_at).toLocaleDateString('pt-BR')} · <StatusBadge status={a.status} />
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    {a.status === 'completed' && (
                      <>
                        <button className="btn-secondary" style={{ fontSize: 'var(--text-caption)' }}
                          onClick={() => navigate(`/profissional/clientes/${clientId}/analise/${a.id}`)}>
                          Ver
                        </button>
                        <button className="btn-secondary" style={{ fontSize: 'var(--text-caption)' }}
                          disabled={downloading === a.id}
                          onClick={() => handleDownload(a)}>
                          {downloading === a.id ? '...' : '⬇ Word'}
                        </button>
                      </>
                    )}
                    <button className="btn-secondary" style={{ fontSize: 'var(--text-caption)' }}
                      disabled={!!generating}
                      onClick={() => handleGenerate(a.service_slug)}>
                      Regerar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB: Relacionamentos */}
      {activeTab === 'Relacionamentos' && (
        <div>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '1rem' }}>
            Vincule familiares ou parceiros para facilitar análises de sinastria e serviços combinados.
          </p>

          {relationships.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
              {relationships.map(rel => (
                <div key={rel.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: '0 0 0.15rem', fontWeight: 600, fontSize: 'var(--text-label)', color: 'var(--text-primary)' }}>
                      {rel.related_client?.name ?? '—'}
                    </p>
                    <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--petrol)' }}>
                      {RELATIONSHIP_LABELS[rel.relationship_type]}
                      {rel.notes ? ` · ${rel.notes}` : ''}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: 'var(--text-caption)', padding: '0.25rem 0.6rem' }}
                      onClick={() => navigate(`/profissional/gerar-analise?clientId=${clientId}&service=synastry&client2Id=${rel.related_client?.id}`)}
                    >
                      Sinastria
                    </button>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: 'var(--text-caption)', padding: '0.25rem 0.6rem', color: '#f87171' }}
                      onClick={() => handleRemoveRelationship(rel.id)}
                    >
                      Remover
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!showRelForm ? (
            <button
              className="btn-secondary"
              style={{ fontSize: 'var(--text-caption)' }}
              onClick={() => setShowRelForm(true)}
              disabled={availableClients.length === 0}
            >
              + Adicionar relacionamento
            </button>
          ) : (
            <div className="card">
              <p style={{ fontWeight: 600, fontSize: 'var(--text-label)', marginBottom: '1rem' }}>Vincular cliente</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  <label style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>
                    Cliente relacionado
                  </label>
                  <select
                    className="form-input"
                    value={relClientId}
                    onChange={e => setRelClientId(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="">Selecione um cliente...</option>
                    {availableClients.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>
                    Tipo de relacionamento
                  </label>
                  <select
                    className="form-input"
                    value={relType}
                    onChange={e => setRelType(e.target.value as RelationshipType)}
                    style={{ width: '100%' }}
                  >
                    {Object.entries(RELATIONSHIP_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>
                    Notas (opcional)
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="Ex: casados desde 2015"
                    value={relNotes}
                    onChange={e => setRelNotes(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="btn-primary"
                    style={{ fontSize: 'var(--text-caption)' }}
                    disabled={!relClientId || savingRel}
                    onClick={handleAddRelationship}
                  >
                    {savingRel ? 'Salvando...' : 'Salvar'}
                  </button>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: 'var(--text-caption)' }}
                    onClick={() => { setShowRelForm(false); setRelClientId(''); setRelNotes(''); }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}

          {availableClients.length === 0 && relationships.length === 0 && (
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
              Cadastre mais clientes para poder vincular relacionamentos.
            </p>
          )}
        </div>
      )}

      {/* TAB: Notas */}
      {activeTab === 'Notas' && (
        <div>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <textarea
              className="form-input"
              placeholder="Escrever nota interna sobre este cliente..."
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              style={{ width: '100%', minHeight: '80px', marginBottom: '0.75rem', resize: 'vertical' }}
            />
            <button className="btn-primary" style={{ fontSize: 'var(--text-caption)' }}
              onClick={handleAddNote} disabled={!noteText.trim()}>
              Adicionar nota
            </button>
          </div>

          {notes.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>Nenhuma nota ainda.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {notes.map(n => (
                <div key={n.id} className="card">
                  <p style={{ margin: '0 0 0.5rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                    {new Date(n.created_at).toLocaleString('pt-BR')}
                  </p>
                  <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                    {n.content}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
